/**
 * 菜单访问级别（只读 / 可操作）
 *
 * 后端 getMenu() 会给每个菜单项带回 accessLevel：
 *   0 = 可操作（默认，兼容存量授权）
 *   1 = 只读，可进入菜单查看与导出，但页面内的写操作被禁用
 *
 * 这里负责把菜单树摊平成「路由路径 -> 访问级别」的映射，供
 * readonly-guard（按钮置灰）和 authrequest（写请求兜底拦截）共用。
 */
import { get, set } from '@/utils/local';
import { getTenantId } from '@/utils/tenant';

export const ACCESS_READ_WRITE = 0;
export const ACCESS_READ_ONLY = 1;

const STORAGE_KEY = 'menuAccess';

// 路由路径 -> 访问级别，内存态，刷新后从 localStorage 恢复
let pathLevelMap = null;

/** 与 AppView.vue 的 resolveItemPath 保持一致：把菜单项解析成路由路径 */
function resolveItemPath(item) {
    if (!item) return '';
    if (item.type == 10) return '/index/report/' + item.id;
    if (item.type == 20) return item.path || '';
    if (item.code) return '/index/' + item.code;
    if (item.path) return item.path;
    return '/index/app/' + item.id;
}

/** 深度遍历菜单树，收集所有叶子节点的路径与访问级别 */
function collect(items, map) {
    if (!items || !items.length) return;
    for (const item of items) {
        const hasChildren = item.items && item.items.length > 0;
        if (hasChildren) {
            // 容器节点（应用/流程组/报表分类）不参与判定，只递归子级
            collect(item.items, map);
            continue;
        }
        const path = resolveItemPath(item);
        if (!path) continue;
        const level = item.accessLevel === ACCESS_READ_ONLY ? ACCESS_READ_ONLY : ACCESS_READ_WRITE;
        // 同一路径被多处菜单命中时取最宽松的一档
        if (map[path] === undefined || level === ACCESS_READ_WRITE) {
            map[path] = level;
        }
    }
}

/** 登录后拿到菜单树时调用，重建映射并落缓存 */
export function setMenuAccess(menuList) {
    const map = {};
    collect(menuList, map);
    pathLevelMap = map;
    set(STORAGE_KEY, map);
    return map;
}

/** 退出登录时清空 */
export function clearMenuAccess() {
    pathLevelMap = null;
    set(STORAGE_KEY, {});
}

function getMap() {
    if (pathLevelMap) return pathLevelMap;
    pathLevelMap = get(STORAGE_KEY) || {};
    return pathLevelMap;
}

/**
 * 查某个路由路径的访问级别。
 * 先精确匹配，再退化为最长前缀匹配（详情页 /index/his/patient/123 归属列表页菜单）。
 */
export function getAccessLevel(path) {
    if (!path) return ACCESS_READ_WRITE;
    const map = getMap();
    if (map[path] !== undefined) return map[path];

    let matched = null;
    for (const menuPath in map) {
        if (!menuPath) continue;
        if (path.indexOf(menuPath + '/') === 0) {
            if (matched === null || menuPath.length > matched.length) {
                matched = menuPath;
            }
        }
    }
    return matched === null ? ACCESS_READ_WRITE : map[matched];
}

/** 该路由是否只读 */
export function isReadonlyPath(path) {
    // lfk 字典对照：有页面入口即可操作，不额外区分只读与维护角色。
    if (path === '/index/hdis-dictionary' && getTenantId() === '3a1f0042-1a62-787f-6cd1-cf138b7e7b77') return false;
    return getAccessLevel(path) === ACCESS_READ_ONLY;
}

/* ------------------------------------------------------------------ *
 * 写请求识别
 *
 * 本项目 209 个 POST 接口读写混用（`POST /api/app-form` 是查询列表，
 * `POST /api/app/patient` 却是新增），单靠 method 判断必然误伤查询。
 * 因此采取「宁可漏拦、绝不误伤」的策略：
 *   - PUT / DELETE / PATCH 一律视为写
 *   - POST 默认放行，只有命中写动词或裸资源写名单才视为写
 * 按钮置灰是主防线，这里只做兜底。
 * ------------------------------------------------------------------ */

/** URL 里出现即可判定为写操作的动词片段 */
const WRITE_KEYWORDS = [
    'save', 'add', 'create', 'insert', 'update', 'edit', 'modify',
    'delete', 'remove', 'submit', 'audit', 'confirm', 'cancel', 'void',
    'revert', 'reverse', 'reversal', 'disable', 'enable', 'apply',
    'complete', 'start-check', 'undo', 'stop', 'generate', 'bind',
    'set-', 'mark-', 'clear-', 'reset', 'repair', 'supplement',
    'refund', 'recharge', 'settle', 'transfer', 'import',
    'sync', 'invite', 'change-', 'link-', 'dismiss', 'register',
    'upload', 'return', 'batch-', '/opt', 'pay-',
    'drug-purchase', 'drug-sale'
];

/** 命中写动词但实际是查询的接口，需显式放行 */
const READ_EXCEPTIONS = [
    'batch-list-by-keys',
    'query-roles-by-menus',
    'check-medicine-availability',
    'settlement-query',
    'settle-qltctrl-query',
    'settlement-statement/detail',
    'settlement-statement/summary',
    'reconciliation/list',
    'load-by-menu-id',
    'load-by-year',
    'decrypt-trace',
    'export',
    'download',
    'dowload',
    'preview',
    'print'
];

/** 裸 RESTful 资源路径，POST 即为新增（URL 里没有任何动词） */
const RESTFUL_CREATE_PATHS = [
    '/api/app/patient',
    '/api/app/doctor',
    '/api/app/department',
    '/api/app/diag',
    '/api/app/dic',
    '/api/app/flow',
    '/api/app/form',
    '/api/app/hospital',
    '/api/app/organization',
    '/api/app/app-role',
    '/api/app/app-manager',
    '/api/app/data-source',
    '/api/app/medicine-stock-adjustment',
    '/api/app/patient-diagnosis',
    '/api/app/app-ext-app-manager',
    '/api/multi-tenancy/tenants'
];

/** 去掉 query string 并转小写 */
function normalizeUrl(url) {
    if (!url) return '';
    const idx = url.indexOf('?');
    return (idx >= 0 ? url.slice(0, idx) : url).toLowerCase();
}

/** 判断一个请求是否属于写操作 */
export function isWriteRequest(url, method) {
    const m = (method || 'get').toLowerCase();
    if (m === 'get' || m === 'head' || m === 'options') return false;
    if (m === 'put' || m === 'delete' || m === 'patch') return true;

    // 剩下的是 POST：默认放行，命中写特征才拦
    const path = normalizeUrl(url);
    if (READ_EXCEPTIONS.some(k => path.indexOf(k) !== -1)) return false;
    if (WRITE_KEYWORDS.some(k => path.indexOf(k) !== -1)) return true;

    const trimmed = path.replace(/\/+$/, '');
    return RESTFUL_CREATE_PATHS.some(p => trimmed === p);
}
